/**
 * DangerousConfirmDialog — 2-step destructive confirm wrapper
 *
 * Step 1: Standard danger ConfirmDialog with summary
 * Step 2: Second modal requiring admin to type "SİL" (TR) / "DELETE" (EN)
 *         before the confirm button is enabled.
 *
 * @example
 * <DangerousConfirmDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   count={selectedIds.length}
 *   onConfirm={handleBulkDelete}
 *   loading={isPending}
 * />
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';

// Step 1 — initial danger confirm
function Step1Dialog({ open, onOpenChange, count, onProceed, t }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-[var(--brand-danger)]">
              <AlertTriangle size={22} />
            </div>
            <div>
              <AlertDialogTitle className="font-display text-lg font-bold text-[var(--text-main)]">
                {t('admin.bulk.confirm.deleteTitle')}
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1 text-sm text-[var(--text-muted)]">
                <span dangerouslySetInnerHTML={{
                  __html: t('admin.bulk.confirm.deleteDesc').replace('{{count}}', `<strong>${count}</strong>`)
                }} />
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 mt-2">
          <AlertDialogCancel className="rounded-xl border-2 border-[var(--border-heavy)] font-bold h-9">
            {t('admin.bulk.confirm.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            className="h-9 bg-[var(--brand-danger)] hover:bg-[var(--brand-danger)]/90 text-white border-[var(--brand-danger-shadow)] border-b-4 rounded-xl font-bold"
            onClick={onProceed}
          >
            {t('admin.bulk.confirm.deleteStep1')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Step 2 — type "SİL" / "DELETE" to unlock final confirm
function Step2Dialog({ open, onOpenChange, onConfirm, loading, t, lang }) {
  const [typed, setTyped] = useState('');
  const expectedWord = lang === 'tr' ? 'SİL' : 'DELETE';
  const isMatch = typed === expectedWord;

  // Reset typed text whenever dialog opens
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl border-2 border-[var(--border-light)] max-w-md">
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-[var(--brand-danger)]">
              <AlertTriangle size={22} />
            </div>
            <div className="flex-1 min-w-0">
              <AlertDialogTitle className="font-display text-lg font-bold text-[var(--text-main)]">
                {t('admin.bulk.confirm.deleteStep2Title')}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="mt-2 space-y-3">
                  <p
                    className="text-sm text-[var(--text-muted)]"
                    dangerouslySetInnerHTML={{
                      __html: t('admin.bulk.confirm.deleteStep2Desc').replace(
                        /\*\*([^*]+)\*\*/g,
                        '<strong>$1</strong>'
                      ),
                    }}
                  />
                  <div className="space-y-1">
                    <Label
                      htmlFor="dangerous-confirm-input"
                      className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide"
                    >
                      {t('admin.bulk.confirm.deleteStep2Placeholder')}
                    </Label>
                    <Input
                      id="dangerous-confirm-input"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      placeholder={t('admin.bulk.confirm.deleteStep2Placeholder')}
                      className="border-2 rounded-xl font-mono text-sm"
                      aria-describedby="dangerous-confirm-hint"
                      autoComplete="off"
                      autoFocus
                    />
                    {typed.length > 0 && !isMatch && (
                      <p
                        id="dangerous-confirm-hint"
                        className="text-xs text-[var(--brand-danger)] font-semibold"
                        role="alert"
                      >
                        {lang === 'tr'
                          ? `"${expectedWord}" yazmanız gerekiyor`
                          : `Type "${expectedWord}" to proceed`}
                      </p>
                    )}
                  </div>
                </div>
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2 mt-2">
          <AlertDialogCancel
            className="rounded-xl border-2 border-[var(--border-heavy)] font-bold h-9"
            disabled={loading}
          >
            {t('admin.bulk.confirm.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            className="h-9 bg-[var(--brand-danger)] hover:bg-[var(--brand-danger)]/90 text-white border-[var(--brand-danger-shadow)] border-b-4 rounded-xl font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={onConfirm}
            disabled={!isMatch || loading}
            aria-disabled={!isMatch || loading}
          >
            {loading
              ? (lang === 'tr' ? 'Siliniyor...' : 'Deleting...')
              : t('admin.bulk.confirm.deleteConfirmBtn')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function DangerousConfirmDialog({
  open,
  onOpenChange,
  count,
  onConfirm,
  loading,
}) {
  const { t, lang } = useLanguage();
  const [step, setStep] = useState(1);

  // Reset step when dialog closes
  useEffect(() => {
    if (!open) setStep(1);
  }, [open]);

  const handleProceedToStep2 = () => {
    setStep(2);
  };

  const handleStep2Close = (isOpen) => {
    if (!isOpen) {
      setStep(1);
      onOpenChange(false);
    }
  };

  if (step === 1) {
    return (
      <Step1Dialog
        open={open}
        onOpenChange={onOpenChange}
        count={count}
        onProceed={handleProceedToStep2}
        t={t}
      />
    );
  }

  return (
    <Step2Dialog
      open={open}
      onOpenChange={handleStep2Close}
      onConfirm={onConfirm}
      loading={loading}
      t={t}
      lang={lang}
    />
  );
}

DangerousConfirmDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

DangerousConfirmDialog.defaultProps = {
  loading: false,
};
